<template>
  <div class="employee-line-menu">
    <MenuIcon
      :dropdownItems="visibleMenuItems"
      @onClick="handleMenuClick"
      :menuWidth="true"
      :isOpen="menuOpen"
    />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted } from 'vue';
import MenuIcon from '@components/MenuIcon/MenuIcon.vue';
import { isReadOnlyImpersonationActive } from '@components/utils/impersonationSession';
import {
  PREVIEW_MODE,
  VIEW_AS_MODE,
  canStartViewAsRef,
  currentUserCanStartViewAs,
  getEmployeeViewAsAction,
  bindCanStartViewAsAccessListener,
} from '@components/utils/viewAsEligibility';
import {
  loadViewAsFeatureFlag,
  viewAsLdEnabled,
} from '@components/utils/viewAsFeatureFlag';

const props = defineProps({
  employee: { type: Object, required: true },
  status: { type: String, default: 'Draft' },
  menuOpen: { type: Boolean, default: false },
  /** Optional override; listing also uses shared LD flag (ER-10317). */
  viewAsFeatureEnabled: { type: Boolean, default: false },
  /** Optional override; defaults to current user's admin permission. */
  canStartViewAs: { type: Boolean, default: false },
});

const emit = defineEmits(['action']);

const app = getCurrentInstance();

onMounted(() => {
  bindCanStartViewAsAccessListener();
  const getFlag = app?.proxy?.$getFlag?.bind(app.proxy);
  if (getFlag) {
    loadViewAsFeatureFlag(getFlag);
  }
});

const visibleMenuItems = computed(() => {
  const status = props.status?.toLowerCase();
  const isArchived = props.employee.archived;
  const accessStatus = (props.employee.accessStatus || props.employee.access?.employee_status || '').toLowerCase();
  const isInvited = accessStatus === 'invited';
  const hasAccess = Boolean(props.employee.hasAccess);
  const isSuspended = Boolean(props.employee.isSuspended);
  const readOnlySession = isReadOnlyImpersonationActive();

  const viewAsEnabled = props.viewAsFeatureEnabled || viewAsLdEnabled.value;
  // Include shared ref so menus recompute after async accessPermission sync.
  const canStart =
    props.canStartViewAs || canStartViewAsRef.value || currentUserCanStartViewAs();

  // Terminate: when status is active (including invite state — user can terminate without activating)
  const showTerminate = status === 'active' && !readOnlySession;
  // Give Access: when active and does not have access and not suspended and not invited (invited users use Resend/Cancel Invite)
  const showGiveAccess =
    status === 'active' && !hasAccess && !isSuspended && !isInvited && !readOnlySession;
  // Restore Access: when active and suspended and has an access record; never show for invited (invited use Resend/Cancel Invite)
  const hasAccessRecord = hasAccess || !!props.employee.access;
  const showRestoreAccess =
    status === 'active' && isSuspended && hasAccessRecord && !isInvited && !readOnlySession;
  // Suspend Access: when active and has access and not suspended; never show for invited
  const showSuspendAccess =
    status === 'active' && hasAccess && !isSuspended && !isInvited && !readOnlySession;
  // Resend/Cancel Invite: only when invited and NOT suspended (suspended users see Restore Access instead)
  const showResendCancelInvite = isInvited && !isSuspended && !readOnlySession;

  const viewAsAction =
    viewAsEnabled && canStart && !readOnlySession
      ? getEmployeeViewAsAction(props.employee, status)
      : null;
  const showImpersonate = viewAsAction === VIEW_AS_MODE;
  const showPreview = viewAsAction === PREVIEW_MODE;

  const rawItems = [
    showImpersonate && {
      name: 'View as',
      value: 'impersonate',
      icon: 'icons/dropdown/access.svg',
    },
    showPreview && {
      name: 'Preview as',
      value: 'previewAsEmployee',
      icon: 'icons/dropdown/access.svg',
    },
    status === 'draft' && !readOnlySession && { name: 'Activate', value: 'activate', icon: 'icons/dropdown/activate.svg' },
    status === 'draft' && !readOnlySession && { name: 'Delete', value: 'delete', icon: 'icons/dropdown/trash.svg', destructive: true, negative: true },
    showResendCancelInvite && { name: 'Resend Invite', value: 'resendInvite', icon: 'icons/dropdown/resend.svg' },
    showResendCancelInvite && { name: 'Cancel Invite', value: 'cancelInvite', icon: 'icons/dropdown/cancel-invite.svg', destructive: true, negative: true },
    showTerminate && { name: 'Terminate', value: 'terminate', icon: 'icons/dropdown/activate.svg' },
    status === 'terminated' && !isArchived && !readOnlySession && { name: 'Reactivate', value: 'reactivate', icon: 'icons/dropdown/activate.svg' },
    !readOnlySession && { name: 'Rename', value: 'rename', icon: 'icons/dropdown/rename.svg' },
    showGiveAccess && { name: 'Give Access', value: 'giveAccess', icon: 'icons/dropdown/access.svg', isWorkflow: true },
    showSuspendAccess && { name: 'Suspend Access', value: 'suspendAccess', icon: 'icons/dropdown/suspendaccess.svg', destructive: true, negative: true },
    showRestoreAccess && { name: 'Restore Access', value: 'restoreAccess', icon: 'icons/dropdown/access.svg' },
    !readOnlySession && { name: 'Change Picture', value: 'changePicture', icon: 'icons/dropdown/change_picture.svg' },
    status === 'terminated' && !isArchived && !readOnlySession && { name: 'Archive', value: 'archive', icon: 'icons/dropdown/archive-new.svg', negative: false },
    isArchived && !readOnlySession && { name: 'Unarchive', value: 'unarchive', icon: 'icons/dropdown/unarchive.svg' }
  ].filter(Boolean);

  // Collapse consecutive separators so we never show two separators in a row
  return rawItems.filter((item, index) => {
    if (!item.separator) return true;
    const prev = rawItems[index - 1];
    return !prev || !prev.separator;
  });
});

function handleMenuClick(item) {
  if (item && item.value) {
    emit('action', { type: item.value, employee: props.employee });
  }
}
</script>
<style scoped>
.employee-line-menu {
  width: 20%;
  text-align: end;
}
</style>
